import CustomButton from '../buttons/CustomButton';
import { ButtonTypes, PaymentResult } from '../../services/exports/Interfaces';
import useDeliveryThreshold from '../../hooks/order/useDeliveryThreshold';
import useCheckoutThreshold from '../../hooks/order/useCheckoutThreshold';
import { useTranslation } from 'react-i18next';
import { useMemo, useRef, useState } from 'react';
import { PaymentMethod, PaymentStatus, SCREENS } from '../../services/exports/Constants';
import useRouter from '../../hooks/navigation/useRouter';
import usePaymentFlow from '../../hooks/payment/usePaymentFlow';
import { SectionType } from './sections/CheckoutInfoBag';
import toast from 'react-hot-toast';
import {
  RenderableErrorResponse,
  ReservedHttpResponseCodes,
  ValidationErrorResponseData,
} from '../../services/exports/Types';
import collect from 'collect.js';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { Modals, Routes } from '../../modules/mobile/exports/Enums';
import useDeliveryOrderMethodValidation from '../../modules/mobile/modals/order-method/useDeliveryOrderMethodValidation';
import useModalRouter from '../../modules/mobile/common/hooks/useModalRouter';
import useOrderErrorHandler from '../../modules/mobile/common/hooks/error-handlers/useOrderErrorHandler';
import Alert from '../../modules/mobile/components/modals/Alert';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import useAlert from '../../hooks/utility/useAlert';
import OrderValidationManager from '../../services/api/OrderValidationManager';
import useOrderCheckout, { ORDER_METHOD_RULES, firstValidationMessage } from '../../hooks/payment/useOrderCheckout';
import useOrderMethodErrorHandlers from '../../hooks/order/useOrderMethodErrorHandlers';
import { isFreeOrderDraft } from '../../services/helpers/checkoutPaymentMethod';

export default function ProceedToPaymentButton({ className }: { className?: string }) {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:ProceedToPaymentButton' });
  const { t: tErrors } = useTranslation(undefined, { keyPrefix: 'Modules:Mobile:Shared:Errors' });

  const { openModal } = useModalRouter();
  const { isMobileApp } = useMobileApp();
  const alert = useAlert();
  const { navigate } = useRouter();
  const orderErrorHandler = useOrderErrorHandler();
  const { toggleCheckoutSection } = usePaymentFlow();
  const { toggleOrderMethodModal } = useAppMethods();
  const { hasReachedCheckoutThreshold } = useCheckoutThreshold();
  const { handleValidationError } = useDeliveryOrderMethodValidation();
  const orderMethodErrorHandlers = useOrderMethodErrorHandlers();
  const { isWithinMinThreshold, isWithinMaxThreshold, isWithinThreshold } = useDeliveryThreshold();
  const {
    data,
    loading: draftLoading,
    isDraftCurrent,
    getOrderData,
    checkOrderNotDuplicate,
    setValidationErrors,
    resetValidationErrors,
  } = useOrderFlow();

  const [loading, setLoading] = useState(false);
  const [isMobileAlertOpen, setIsMobileAlertOpen] = useState(false);
  const mobileAppGenericError = useRef(null);
  const toggleMobileAlert = () => setIsMobileAlertOpen((prev) => !prev);

  const { checkout } = useOrderCheckout({
    setLoading,
    onMobileAppCheckoutError: (response) => {
      mobileAppGenericError.current =
        response.response?.data?.description || response.response?.data?.title || tErrors('internal_error');
      toggleMobileAlert();
    },
  });

  const onInputSectionError = (section: SectionType): void => {
    toast.error(t('toasts.input_errors_found'));

    toggleCheckoutSection(section, true);
  };

  const onValidationError = (response: ValidationErrorResponseData): void => {
    setValidationErrors(response);
    setLoading(false);

    // Navigate to personal info section on validation error
    if (
      collect(response.rules)
        .only(['customer_first_name', 'customer_last_name', 'customer_email', 'customer_phone_number'])
        .isNotEmpty()
    ) {
      return onInputSectionError(SectionType.customer_info);
    }

    // Navigate to invoice info section on validation error
    if (collect(response.rules).only(['invoice_email']).isNotEmpty()) {
      return onInputSectionError(SectionType.invoice_info);
    }

    if (collect(response.rules).only(ORDER_METHOD_RULES).isNotEmpty()) {
      return toggleOrderMethodModal({ openModal: true });
    }

    toast.error(firstValidationMessage(response) ?? t('toasts.input_errors_found'));
  };

  const onMobileAppError = (response: RenderableErrorResponse) => {
    const validationError = handleValidationError(response, tErrors('internal_error'));

    setLoading(false);

    if (validationError) {
      return openModal({
        id: Modals.OrderMethod,
        props: {
          deliveryAddressError: validationError.deliveryAddress,
          scheduledForError: validationError.scheduledFor,
        },
      });
    }

    orderErrorHandler(response);
    mobileAppGenericError.current =
      response.response?.data?.description || response.response?.data?.title || tErrors('internal_error');
    toggleMobileAlert();
  };

  const title = useMemo(() => {
    if (!hasReachedCheckoutThreshold || !isWithinMinThreshold) {
      return t('add_items');
    }

    if (!isWithinMaxThreshold) {
      return t('remove_items');
    }

    if (data?.total === 0) {
      return t('place_order');
    }

    // No amount: the overview carries no price at all, so a figure here would be the only one on
    // the screen and would not match what the payment page charges. Every gate around this button
    // still reads the real `data.total`.
    return t('proceed_to_payment');
  }, [i18n.language, isWithinMinThreshold, isWithinMaxThreshold, hasReachedCheckoutThreshold, data?.total]);

  const onClick = async (): Promise<void> => {
    if (!isWithinThreshold || !hasReachedCheckoutThreshold) {
      return navigate({
        web: {
          to: SCREENS.HOME_SCREEN,
        },
      });
    }

    if (!(await checkOrderNotDuplicate())) {
      return;
    }

    // Only the draft that matches the basket right now can say the order is free. A stale or failed
    // draft can still hold a zero from before the basket changed, and placing that as cash charges
    // the customer a method they never chose; it goes through validation and /payment instead.
    if (isFreeOrderDraft(data?.total, isDraftCurrent)) {
      const confirmPayment = async (): Promise<PaymentResult> => ({
        status: PaymentStatus.Succeeded,
        error: null,
        result: null,
      });

      await checkout(confirmPayment, null, {
        payment_method_id: PaymentMethod.Cash,
        payment_method_source: 'free_order',
      });

      return;
    }

    resetValidationErrors();
    // A 412 naming the order method (slot gone, pickup closed, ...) is fixed in the order-method
    // modal, not on the menu — without these handlers the InfoModal's confirm falls back to HOME.
    const { success, response, status } = await OrderValidationManager.validate(
      {
        ...getOrderData(),
        full_mode: true,
      },
      orderMethodErrorHandlers,
    );

    if (success) {
      return navigate({
        web: {
          to: {
            pathname: `${SCREENS.PAYMENT}`,
          },
        },
        mobile: {
          path: Routes.Payment,
        },
      });
    }

    if (status === ReservedHttpResponseCodes.ValidationError) {
      return onValidationError(response.response.data);
    }

    if (isMobileApp) {
      return onMobileAppError(response as any as RenderableErrorResponse);
    }

    if (
      status === ReservedHttpResponseCodes.ErrorResponseModal ||
      status === ReservedHttpResponseCodes.ErrorResponseAlertWindow
    ) {
      return;
    }

    alert.error({
      title: t('error_messages.internal_error.title'),
      description: t('error_messages.internal_error.description'),
    });
  };

  const _onClick = async () => {
    setLoading(true);
    await onClick();
    setLoading(false);
  };

  return (
    <>
      <Alert
        title={mobileAppGenericError.current}
        isOpen={isMobileAlertOpen}
        onDidDismiss={toggleMobileAlert}
        buttons={[
          {
            text: t('go_to_menu'),
            handler: () =>
              navigate({
                mobile: {
                  path: Routes.Main,
                  payload: {
                    tab: 'Menu',
                  },
                },
              }),
          },
        ]}
      />
      {/*
        Why this CTA needs a state marker.
        `onClick` has four outcomes and, before this, the DOM could not tell them apart:
          * threshold not met      -> navigates HOME (no request at all)
          * duplicate order        -> returns silently (guest-skipped)
          * draft total is 0       -> cash checkout, no validation (only while the draft is current,
                                      which `data-draft-current` reports)
          * otherwise              -> validates, then goes to /payment
        `silentlyDisabled` also never reaches the DOM, so a click landing during an in-flight draft
        is a no-op. An e2e that clicks and waits for a request therefore hangs for the full test
        timeout with nothing naming the cause — which is exactly what happened to E1 in CI.
        `data-ready` is the single precondition a spec should wait on; `data-blocked-by` says why
        when it is false.
      */}
      <span
        data-test="proceed-to-payment-state"
        data-ready={isWithinThreshold && hasReachedCheckoutThreshold && !draftLoading ? 'true' : 'false'}
        data-blocked-by={
          !hasReachedCheckoutThreshold
            ? 'checkout-threshold'
            : !isWithinThreshold
              ? 'delivery-threshold'
              : draftLoading
                ? 'draft-loading'
                : undefined
        }
        data-draft-total={data?.total}
        data-draft-current={isDraftCurrent ? 'true' : 'false'}
        className="hidden"
      />
      <CustomButton
        // CustomButton renders data-test from testId, so no component change is needed there.
        testId="proceed-to-payment-button"
        buttonType={!isWithinThreshold || !hasReachedCheckoutThreshold ? ButtonTypes.forth : ButtonTypes.primary}
        title={title}
        onClick={_onClick}
        loading={loading}
        silentlyDisabled={draftLoading}
        className={className}
        showArrow
      />
    </>
  );
}
